/**
 * usePOSParkedSales - Custom hook for POS parked sales management
 * Handles park, resume, release, delete operations with server persistence
 */
import { useState, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export function usePOSParkedSales(staffId) {
  const [parkedSales, setParkedSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentParkedSaleId, setCurrentParkedSaleId] = useState(null);

  // Fetch all parked sales
  const fetchParkedSales = useCallback(async () => {
    if (!staffId) return;
    
    setLoading(true);
    try {
      const res = await api.get('/api/parked-sales/list', {
        headers: { 'X-Staff-ID': staffId }
      });
      
      if (res.data.success) {
        // Filter to only show available (not claimed by others) and own claimed sales
        const sales = res.data.parked_sales || [];
        const availableSales = sales.filter(sale => 
          sale.status === 'available' || 
          (sale.status === 'claimed' && sale.claimed_by === staffId)
        );
        setParkedSales(availableSales);
      }
    } catch (err) {
      console.error('Failed to fetch parked sales:', err);
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  // Auto-fetch on staffId change
  useEffect(() => {
    if (staffId) {
      fetchParkedSales();
    }
  }, [staffId, fetchParkedSales]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    if (!staffId) return;
    
    const interval = setInterval(fetchParkedSales, 30000);
    return () => clearInterval(interval);
  }, [staffId, fetchParkedSales]);

  // Park current sale
  const parkSale = useCallback(async ({
    cart,
    customer,
    customerDiscount,
    customerCoffeePass,
    appliedCashierDiscount,
    orderType,
    tableNumber
  }) => {
    if (!cart || cart.length === 0) {
      toast.error('No items in cart to park');
      return { success: false };
    }
    
    // Calculate totals
    const subtotal = cart.reduce((sum, item) => {
      const itemPrice = item.price || 0;
      const addOnsTotal = (item.add_ons || []).reduce((aSum, a) => aSum + (a.price || 0) * (a.quantity || 1), 0);
      return sum + ((itemPrice + addOnsTotal) * (item.quantity || 1));
    }, 0);
    
    // Apply discounts
    let discountTotal = 0;
    if (customerDiscount?.discount > 0) {
      discountTotal += subtotal * (customerDiscount.discount / 100);
    }
    if (appliedCashierDiscount?.value > 0) {
      if (appliedCashierDiscount.type === 'percentage') {
        discountTotal += subtotal * (appliedCashierDiscount.value / 100);
      } else {
        discountTotal += appliedCashierDiscount.value;
      }
    }
    
    const taxRate = 0.125;
    const afterDiscount = Math.max(0, subtotal - discountTotal);
    const taxAmount = afterDiscount * taxRate / (1 + taxRate);
    const total = afterDiscount;
    
    const payload = {
      cart_items: cart.map(item => ({
        item_id: item.item_id || item.id,
        name: item.name,
        price: item.price,
        quantity: item.quantity || 1,
        category: item.category,
        size: item.size,
        selectedSize: item.selectedSize,
        add_ons: item.add_ons || [],
        special_instructions: item.special_instructions,
        total_price: (item.price + (item.add_ons || []).reduce((s, a) => s + (a.price || 0) * (a.quantity || 1), 0)) * (item.quantity || 1),
        is_coffee_pass_redemption: item.is_coffee_pass_redemption || false
      })),
      customer: customer ? {
        id: customer.id,
        name: customer.name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim(),
        first_name: customer.first_name,
        last_name: customer.last_name,
        phone: customer.phone,
        email: customer.email
      } : null,
      customer_discount: customerDiscount,
      customer_coffee_pass: customerCoffeePass,
      cashier_discount: appliedCashierDiscount,
      order_type: orderType,
      table_number: tableNumber,
      subtotal: subtotal,
      tax_amount: taxAmount,
      discount_amount: discountTotal,
      total: total
    };
    
    try {
      const res = await api.post('/api/parked-sales/create', payload, {
        headers: { 'X-Staff-ID': staffId }
      });
      
      if (res.data.success) {
        fetchParkedSales();
        toast.success('Sale parked successfully', {
          description: `${cart.length} item(s) held for ${customer?.name || customer?.first_name || 'Guest'}`
        });
        return { success: true };
      }
      return { success: false };
    } catch (err) {
      console.error('Failed to park sale:', err);
      toast.error('Failed to park sale', {
        description: err.response?.data?.detail || 'Server error - your cart is still here'
      });
      return { success: false };
    }
  }, [staffId, fetchParkedSales]);

  // Resume a parked sale
  const resumeParkedSale = useCallback(async (parkedSaleId) => {
    try {
      const res = await api.post('/api/parked-sales/claim', 
        { parked_sale_id: parkedSaleId },
        { headers: { 'X-Staff-ID': staffId } }
      );
      
      if (res.data.success) {
        const sale = res.data.parked_sale;
        setCurrentParkedSaleId(parkedSaleId);
        fetchParkedSales();
        
        toast.success('Sale resumed', {
          description: `Continuing order for ${sale.customer?.name || sale.customer?.first_name || 'Guest'}`
        });
        
        return { 
          success: true, 
          sale: sale,
          cartItems: sale.cart_items,
          customer: sale.customer,
          customerDiscount: sale.customer_discount,
          customerCoffeePass: sale.customer_coffee_pass,
          cashierDiscount: sale.cashier_discount,
          orderType: sale.order_type,
          tableNumber: sale.table_number
        };
      }
      return { success: false };
    } catch (err) {
      console.error('Failed to resume parked sale:', err);
      toast.error('Cannot resume sale', {
        description: err.response?.data?.detail || 'Someone else may be using this sale'
      });
      return { success: false };
    }
  }, [staffId, fetchParkedSales]);

  // Release a parked sale (return to pool)
  const releaseParkedSale = useCallback(async (parkedSaleId) => {
    try {
      await api.post('/api/parked-sales/release',
        { parked_sale_id: parkedSaleId },
        { headers: { 'X-Staff-ID': staffId } }
      );
      
      if (currentParkedSaleId === parkedSaleId) {
        setCurrentParkedSaleId(null);
      }
      
      fetchParkedSales();
      toast.info('Sale released - available for others');
      return { success: true, wasCurrentSale: currentParkedSaleId === parkedSaleId };
    } catch (err) {
      console.error('Failed to release parked sale:', err);
      toast.error('Failed to release sale');
      return { success: false };
    }
  }, [staffId, currentParkedSaleId, fetchParkedSales]);

  // Delete a parked sale
  const deleteParkedSale = useCallback(async (parkedSaleId, reason = 'Cancelled by staff', skipConfirm = false) => {
    if (!skipConfirm && !window.confirm('Are you sure you want to cancel this parked sale? This cannot be undone.')) {
      return { success: false, cancelled: true };
    }
    
    try {
      await api.post('/api/parked-sales/cancel',
        { parked_sale_id: parkedSaleId, reason: reason },
        { headers: { 'X-Staff-ID': staffId } }
      );
      
      if (currentParkedSaleId === parkedSaleId) {
        setCurrentParkedSaleId(null);
      }
      
      fetchParkedSales();
      toast.info('Parked sale cancelled');
      return { success: true, wasCurrentSale: currentParkedSaleId === parkedSaleId };
    } catch (err) {
      console.error('Failed to cancel parked sale:', err);
      toast.error('Failed to cancel sale', {
        description: err.response?.data?.detail || 'You may need manager authorization'
      });
      return { success: false };
    }
  }, [staffId, currentParkedSaleId, fetchParkedSales]);

  // Mark parked sale as completed
  const completeParkedSale = useCallback(async (orderId) => {
    if (!currentParkedSaleId) return { success: true };
    
    try {
      await api.post('/api/parked-sales/complete',
        { parked_sale_id: currentParkedSaleId, order_id: orderId },
        { headers: { 'X-Staff-ID': staffId } }
      );
      
      setCurrentParkedSaleId(null);
      fetchParkedSales();
      return { success: true };
    } catch (err) {
      console.error('Failed to mark parked sale complete:', err);
      // Don't show error - the order was successful, this is just cleanup
      return { success: false };
    }
  }, [staffId, currentParkedSaleId, fetchParkedSales]);

  return {
    parkedSales,
    loading,
    currentParkedSaleId,
    setCurrentParkedSaleId,
    fetchParkedSales,
    parkSale,
    resumeParkedSale,
    releaseParkedSale,
    deleteParkedSale,
    completeParkedSale,
    hasParkedSales: parkedSales.length > 0,
    count: parkedSales.length
  };
}

export default usePOSParkedSales;
